(() => {
  'use strict';

  const q = (s, r = document) => r.querySelector(s);
  const qa = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const emit = (event, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`bb:${event}`, { detail }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `bb_${event}`, ...detail });
  };

  /* ---------------------------------------------------------
     V1.4 Work Theater: preserve the changing-screen behavior on mobile
     without restoring the old full-viewport overlap failure.
     --------------------------------------------------------- */
  qa('[data-work-theater]').forEach((root) => {
    const screen = q('[data-work-screen]', root);
    const title = q('[data-work-screen-title]', root);
    if (!screen || !title || !('MutationObserver' in window)) return;
    let timer = 0;
    const pulse = () => {
      if (reduce) return;
      clearTimeout(timer);
      screen.classList.remove('is-mobile-changing');
      void screen.offsetWidth;
      screen.classList.add('is-mobile-changing');
      timer = setTimeout(() => screen.classList.remove('is-mobile-changing'), 500);
    };
    new MutationObserver(pulse).observe(title, { childList: true, characterData: true, subtree: true });
  });

  /* ---------------------------------------------------------
     Growth MRI — self-reported diagnostic snapshot.
     No fake "processing" delay: results are calculated locally and are
     available immediately after the final answer. The game is optional.
     --------------------------------------------------------- */
  const axisLabels = {
    visibility: 'Visibility',
    demand: 'Demand',
    experience: 'Experience',
    measurement: 'Measurement',
    followthrough: 'Follow-through'
  };

  const diagnoses = {
    visibility: {
      title: 'Qualified buyers may not be finding you reliably.',
      move: 'Map commercial search, local/entity coverage, and the pages that should own each high-value buyer question.',
      proof: 'Search visibility by intent, qualified organic/local leads, and the pages/queries attached to real opportunities.'
    },
    demand: {
      title: 'Demand may be reaching the business without a strong buying reason.',
      move: 'Tighten offer-to-audience fit, conversion signals, and paid/organic message alignment before buying more traffic.',
      proof: 'Qualified conversion rate, cost per qualified opportunity, and downstream sales acceptance by source.'
    },
    experience: {
      title: 'The buying experience may be creating avoidable uncertainty.',
      move: 'Rebuild the highest-intent journey around the buyer decision, proof burden, mobile experience, and next action.',
      proof: 'Qualified conversion rate, mobile completion, interaction drop-off, and task success on priority journeys.'
    },
    measurement: {
      title: 'The business may be optimizing from incomplete or untrusted signals.',
      move: 'Create one measurement map from source → conversion → CRM outcome, then remove duplicate and low-value events.',
      proof: 'Event accuracy, source-to-opportunity traceability, lead quality by channel, and attribution confidence.'
    },
    followthrough: {
      title: 'Good opportunities may be losing momentum after the conversion.',
      move: 'Make stage, owner, blocker, response-time target, and next action visible for every qualified opportunity.',
      proof: 'Speed-to-lead, next-action coverage, stage aging, opportunity progression, and closed-loop feedback to marketing.'
    }
  };

  const initializedMriRoots = new WeakSet();

  function initMri(root) {
    if (!root || initializedMriRoots.has(root)) return;
    initializedMriRoots.add(root);

    const steps = qa('[data-mri-question]', root);
    const progressItems = qa('[data-mri-progress-item]', root);
    const game = q('[data-mri-game]', root);
    const report = q('[data-mri-report]', root);
    const ready = q('[data-mri-ready]', root);
    const nextButton = q('[data-mri-next]', root);
    const backButton = q('[data-mri-back]', root);
    const status = q('[data-mri-status]', root);
    const openReportButtons = qa('[data-mri-open-report]', root);
    const playGameButton = q('[data-mri-play-game]', root);
    const restartButton = q('[data-mri-restart]', root);
    const answers = new Map();
    let current = 0;
    let started = false;

    const showQuestion = (index) => {
      current = Math.max(0, Math.min(index, steps.length - 1));
      steps.forEach((step, i) => step.classList.toggle('is-active', i === current));
      progressItems.forEach((item, i) => {
        item.classList.toggle('is-active', i === current);
        item.classList.toggle('is-done', answers.has(i));
      });
      if (backButton) backButton.hidden = current === 0;
      if (nextButton) {
        nextButton.hidden = false;
        nextButton.textContent = current === steps.length - 1 ? 'Build my snapshot →' : 'Next question →';
        nextButton.disabled = !answers.has(current);
      }
      if (status) status.textContent = `QUESTION ${String(current + 1).padStart(2, '0')} / ${String(steps.length).padStart(2, '0')} · SELF-REPORTED INPUT`;
      q('.bb-mri-option.is-selected', steps[current])?.focus({ preventScroll: true });
    };

    qa('[data-mri-option]', root).forEach((button) => {
      button.addEventListener('click', () => {
        const step = button.closest('[data-mri-question]');
        const i = steps.indexOf(step);
        qa('[data-mri-option]', step).forEach((b) => b.classList.toggle('is-selected', b === button));
        if (!started) { started = true; emit('mri_started'); }
        answers.set(i, {
          axis: button.dataset.axis || step.dataset.axis || 'visibility',
          score: Number(button.dataset.score || 0),
          label: button.dataset.answer || button.textContent.trim()
        });
        progressItems[i]?.classList.add('is-done');
        if (nextButton && i === current) nextButton.disabled = false;
      });
    });

    const calculate = () => {
      const totals = {};
      const counts = {};
      Object.keys(axisLabels).forEach((axis) => { totals[axis] = 0; counts[axis] = 0; });
      answers.forEach((answer) => {
        if (!(answer.axis in totals)) return;
        totals[answer.axis] += answer.score;
        counts[answer.axis] += 1;
      });
      const scores = {};
      Object.keys(axisLabels).forEach((axis) => {
        scores[axis] = counts[axis] ? Math.round((totals[axis] / (counts[axis] * 3)) * 100) : 0;
      });
      const overall = Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / Object.keys(scores).length);
      const weakest = Object.keys(scores).sort((a, b) => scores[a] - scores[b])[0];
      return { scores, overall, weakest };
    };

    const fillReport = () => {
      const result = calculate();
      const diagnosis = diagnoses[result.weakest];
      const scoreEl = q('[data-mri-overall]', root);
      const weakestEl = q('[data-mri-weakest]', root);
      const headlineEl = q('[data-mri-diagnosis-headline]', root);
      const moveEl = q('[data-mri-first-move]', root);
      const proofEl = q('[data-mri-proof]', root);
      if (scoreEl) scoreEl.textContent = result.overall;
      if (weakestEl) weakestEl.textContent = axisLabels[result.weakest];
      if (headlineEl) headlineEl.textContent = diagnosis.title;
      if (moveEl) moveEl.textContent = diagnosis.move;
      if (proofEl) proofEl.textContent = diagnosis.proof;
      Object.keys(result.scores).forEach((axis) => {
        const row = q(`[data-mri-axis="${axis}"]`, root);
        if (!row) return;
        q('[data-mri-axis-score]', row).textContent = `${result.scores[axis]}`;
        q('[data-mri-axis-fill]', row).style.width = `${result.scores[axis]}%`;
      });
      const summary = `Growth MRI self-reported snapshot — Overall: ${result.overall}/100. Weakest axis: ${axisLabels[result.weakest]}. Visibility ${result.scores.visibility}/100; Demand ${result.scores.demand}/100; Experience ${result.scores.experience}/100; Measurement ${result.scores.measurement}/100; Follow-through ${result.scores.followthrough}/100. First move: ${diagnosis.move}`;
      const hiddenSummary = q('[data-mri-summary-field]', root);
      const hiddenScore = q('[data-mri-score-field]', root);
      if (hiddenSummary) hiddenSummary.value = summary;
      if (hiddenScore) hiddenScore.value = `${result.overall}`;
      return result;
    };

    const revealReport = () => {
      fillReport();
      steps.forEach((step) => step.classList.remove('is-active'));
      ready?.classList.remove('is-active');
      game?.classList.remove('is-active');
      report?.classList.add('is-active');
      const reportResult = calculate();
      emit('mri_report_opened', { score: reportResult.overall, weakest_axis: reportResult.weakest });
      if (nextButton) nextButton.hidden = true;
      if (backButton) backButton.hidden = true;
      if (status) status.textContent = 'SNAPSHOT READY · SELF-REPORTED · HUMAN REVIEW OPTIONAL';
      report?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
    };

    const showReady = () => {
      fillReport();
      steps.forEach((step) => step.classList.remove('is-active'));
      progressItems.forEach((item) => item.classList.add('is-done'));
      if (nextButton) nextButton.hidden = true;
      if (backButton) backButton.hidden = true;
      ready?.classList.add('is-active');
      const result = calculate();
      emit('mri_completed', { score: result.overall, weakest_axis: result.weakest });
      if (status) status.textContent = 'SNAPSHOT CALCULATED LOCALLY · NO FAKE WAITING SCREEN';
      ready?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
    };

    nextButton?.addEventListener('click', () => {
      if (!answers.has(current)) return;
      if (current < steps.length - 1) showQuestion(current + 1);
      else showReady();
    });
    backButton?.addEventListener('click', () => showQuestion(current - 1));
    openReportButtons.forEach((btn) => btn.addEventListener('click', revealReport));

    /* Bee Bee Signal Sweep — optional, accessible, non-blocking. */
    const gameCards = [
      ['Qualified opportunity created', 'signal', 'A commercial outcome with a buyer attached.'],
      ['Untracked phone calls', 'leak', 'Real demand exists, but attribution/context disappears.'],
      ['CTR with no lead-quality context', 'noise', 'Useful diagnostic metric; weak business outcome by itself.'],
      ['Same-day qualified lead response', 'signal', 'A buyer signal protected by follow-through.'],
      ['Form submits with no CRM owner', 'leak', 'The conversion happened, but responsibility vanished.'],
      ['10,000 scraped rows before ICP rules', 'noise', 'Volume without qualification is usually inventory, not pipeline.'],
      ['Search query → booked opportunity', 'signal', 'Discovery can be connected to a real sales outcome.'],
      ['Duplicate conversion events in ad reporting', 'leak', 'The system may optimize toward a false signal.'],
      ['A 92 Lighthouse score with zero buyer clarity', 'noise', 'Performance matters; it is not the whole buying experience.'],
      ['Opportunity has stage + blocker + next action', 'signal', 'The business knows what must happen next.']
    ];
    let gameIndex = 0;
    let gameScore = 0;
    let gameStartedAt = 0;
    let gameTimer = 0;
    let gameLocked = false;
    const cardEl = q('[data-game-card]', root);
    const scoreGameEl = q('[data-game-score]', root);
    const countGameEl = q('[data-game-count]', root);
    const timeGameEl = q('[data-game-time]', root);
    const noteGameEl = q('[data-game-note]', root);

    const renderGame = () => {
      const card = gameCards[gameIndex];
      if (cardEl) cardEl.textContent = card ? card[0] : 'Signal Sweep complete.';
      if (scoreGameEl) scoreGameEl.textContent = String(gameScore);
      if (countGameEl) countGameEl.textContent = `${Math.min(gameIndex + 1, gameCards.length)}/${gameCards.length}`;
    };

    const endGame = () => {
      clearInterval(gameTimer);
      if (noteGameEl) noteGameEl.textContent = `RUN COMPLETE · ${gameScore}/${gameCards.length}. MRI result was already ready — the game never changes the diagnosis.`;
      emit('mri_game_completed', { game_score: gameScore, game_total: gameCards.length });
      qa('[data-game-choice]', root).forEach((b) => b.disabled = true);
      setTimeout(revealReport, reduce ? 0 : 650);
    };

    const startGame = () => {
      ready?.classList.remove('is-active');
      game?.classList.add('is-active');
      emit('mri_game_started');
      report?.classList.remove('is-active');
      gameIndex = 0; gameScore = 0; gameStartedAt = Date.now(); gameLocked = false;
      qa('[data-game-choice]', root).forEach((b) => b.disabled = false);
      if (noteGameEl) noteGameEl.textContent = '1 = SIGNAL · 2 = LEAK · 3 = NOISE';
      renderGame();
      clearInterval(gameTimer);
      gameTimer = setInterval(() => {
        const left = Math.max(0, 30 - Math.floor((Date.now() - gameStartedAt) / 1000));
        if (timeGameEl) timeGameEl.textContent = `${left}s`;
        if (left <= 0) endGame();
      }, 250);
      q('[data-game-choice]', root)?.focus();
    };

    const answerGame = (choice) => {
      if (!game?.classList.contains('is-active') || gameLocked) return;
      const card = gameCards[gameIndex];
      if (!card) return;
      gameLocked = true;
      qa('[data-game-choice]', root).forEach((b) => b.disabled = true);
      const correct = choice === card[1];
      if (correct) gameScore += 1;
      if (noteGameEl) noteGameEl.textContent = `${correct ? 'CLEAN READ' : `BEE BEE: ${card[1].toUpperCase()}` } · ${card[2]}`;
      gameIndex += 1;
      if (gameIndex >= gameCards.length) return setTimeout(endGame, 350);
      setTimeout(() => {
        renderGame();
        gameLocked = false;
        qa('[data-game-choice]', root).forEach((b) => b.disabled = false);
      }, 220);
    };

    playGameButton?.addEventListener('click', startGame);
    qa('[data-game-choice]', root).forEach((btn) => btn.addEventListener('click', () => answerGame(btn.dataset.gameChoice)));
    root.addEventListener('keydown', (e) => {
      if (!game?.classList.contains('is-active')) return;
      const map = { '1': 'signal', '2': 'leak', '3': 'noise' };
      if (map[e.key]) { e.preventDefault(); answerGame(map[e.key]); }
    });

    restartButton?.addEventListener('click', () => {
      clearInterval(gameTimer);
      answers.clear();
      started = false;
      emit('mri_restarted');
      qa('[data-mri-option]', root).forEach((b) => b.classList.remove('is-selected'));
      ready?.classList.remove('is-active');
      game?.classList.remove('is-active');
      report?.classList.remove('is-active');
      showQuestion(0);
    });

    showQuestion(0);
  }

  qa('[data-mri-terminal]').forEach(initMri);
  qa('#GrowthMriContact').forEach((form) => form.addEventListener('submit', () => emit('mri_review_requested')));
  qa('#WorkshopRequestForm').forEach((form) => form.addEventListener('submit', () => emit('workshop_request_submitted')));
  document.addEventListener('shopify:section:load', (e) => {
    const root = q('[data-mri-terminal]', e.target);
    if (root) initMri(root);
  });

  /* Team cards: motion only on fine pointer, no accessibility dependency. */
  if (matchMedia('(pointer:fine)').matches && !reduce) {
    qa('[data-team-card]').forEach((card) => {
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - .5;
        const y = (e.clientY - r.top) / r.height - .5;
        card.style.transform = `perspective(1100px) rotateX(${(-y * 4).toFixed(2)}deg) rotateY(${(x * 5).toFixed(2)}deg) translateY(-6px)`;
        card.style.boxShadow = '0 34px 80px rgba(7,23,43,.14)';
      });
      card.addEventListener('pointerleave', () => { card.style.transform = ''; card.style.boxShadow = ''; });
    });
  }
})();
